import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { History, RotateCcw, Eye } from "lucide-react";

/**
 * StageVersions — version history strip shown beneath any stage that supports
 * regeneration (Concepts, Script, Storyboard, etc.). Reads from
 * GET /api/projects/{pid}/stages/{stage}/versions and offers View / Restore.
 *
 * Strict scope: this component does NOT trigger regeneration. Restore is a
 * data-only action — the live output becomes a new version, the chosen
 * historical version becomes the live output. Nothing gets reset downstream.
 */
export default function StageVersions({ project, stageKey, onRestored }) {
    const [meta, setMeta] = useState({ current: null, versions: [] });
    const [loading, setLoading] = useState(true);
    const [busyIdx, setBusyIdx] = useState(null);
    const [viewing, setViewing] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/projects/${project.id}/stages/${stageKey}/versions`);
            setMeta(data);
        } catch (e) {
            // 404 → no project; otherwise just leave empty silently
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [project.id, stageKey]);

    const view = async (idx) => {
        try {
            const { data } = await api.get(`/projects/${project.id}/stages/${stageKey}/versions/${idx}`);
            setViewing(data);
        } catch {
            toast.error("Could not load that version");
        }
    };

    const restore = async (idx, label) => {
        if (!window.confirm(`Restore ${label}? The current output will be saved as a new version.`)) return;
        setBusyIdx(idx);
        try {
            await api.post(`/projects/${project.id}/stages/${stageKey}/versions/${idx}/restore`);
            toast.success(`${label} restored as the current ${stageKey.replace("_", " ")}`);
            await refresh();
            onRestored?.();
        } catch (e) {
            toast.error(e?.userMessage || "Restore failed");
        } finally {
            setBusyIdx(null);
        }
    };

    if (loading) return null;
    const all = [...(meta.versions || [])];
    if (!all.length && !meta.current?.size) return null;

    return (
        <div className="mt-6 border border-zinc-900 rounded-sm" data-testid={`versions-${stageKey}`}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900">
                <div className="flex items-center gap-2">
                    <History className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="label-micro">Version history</span>
                    <span className="text-[10px] font-mono text-zinc-600">
                        {all.length + (meta.current?.size ? 1 : 0)} total
                    </span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-600">
                    Regenerating saves a new version
                </span>
            </div>
            <div className="divide-y divide-zinc-900">
                {meta.current?.size ? (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-orange-500/[0.04]" data-testid={`version-current-${stageKey}`}>
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-orange-400">{meta.current.label}</span>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-orange-500/30 text-orange-300 rounded-sm">
                                Current · {meta.current.status || "pending"}
                            </span>
                            <span className="text-[10px] text-zinc-600">{meta.current.saved_at?.slice(0, 19).replace("T", " ")}</span>
                        </div>
                        <span className="text-[10px] text-zinc-600">live output</span>
                    </div>
                ) : null}
                {all.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-zinc-500">
                        No prior versions yet — the next regeneration will save the current output as v1.
                    </div>
                ) : null}
                {all.slice().reverse().map((v) => (
                    <div key={v.index} className="flex items-center justify-between px-4 py-2.5"
                        data-testid={`version-row-${stageKey}-${v.index}`}>
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs font-mono text-zinc-300">{v.label}</span>
                            <span className="text-[10px] text-zinc-600">
                                {(v.regenerated_at || v.saved_at || "").slice(0, 19).replace("T", " ")}
                            </span>
                            {v.regenerated_by && (
                                <span className="text-[10px] text-zinc-600 truncate max-w-[160px]">by {v.regenerated_by}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid={`version-view-${stageKey}-${v.index}`}
                                onClick={() => view(v.index)}
                                className="text-xs text-zinc-400 hover:text-zinc-100 border border-zinc-800 hover:border-zinc-600 px-2 py-1 rounded-sm inline-flex items-center gap-1">
                                <Eye className="w-3 h-3" /> View
                            </button>
                            <button
                                data-testid={`version-restore-${stageKey}-${v.index}`}
                                disabled={busyIdx === v.index}
                                onClick={() => restore(v.index, v.label)}
                                className="text-xs text-orange-300 hover:text-orange-200 border border-orange-500/30 hover:border-orange-500/60 px-2 py-1 rounded-sm inline-flex items-center gap-1 disabled:opacity-50">
                                <RotateCcw className="w-3 h-3" /> {busyIdx === v.index ? "Restoring…" : "Restore"}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {viewing && (
                <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-6"
                    onClick={() => setViewing(null)} data-testid={`version-viewer-${stageKey}`}>
                    <div className="card-base p-5 max-w-3xl w-full max-h-[80vh] overflow-auto"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="label-micro">{stageKey.replace("_", " ")} · {viewing.label}</div>
                                <div className="text-xs text-zinc-500">{viewing.saved_at}</div>
                            </div>
                            <button onClick={() => setViewing(null)} className="text-zinc-500 hover:text-zinc-200 text-sm">Close</button>
                        </div>
                        <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-words">
                            {JSON.stringify(viewing.output, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
